import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import { AuthShell } from "@/components/shared/AuthShell";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Email links to `/auth/verify-root-invite?token=...` (platform admin invite).
 * POSTs the token to the API and stores the platform session (no tenant).
 */
export function VerifyRootInvitePage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const { verifyRootInviteAndLogin, isAuthenticated, isLoading } = useAuth();
  const { t } = useTranslation();
  const [status, setStatus] = useState<"pending" | "success" | "error">("pending");
  const startedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!token) {
      toast.error(t("auth.verify.error"));
      setStatus("error");
      return;
    }
    if (isLoading || isAuthenticated) return;
    if (startedRef.current) return;
    startedRef.current = true;

    verifyRootInviteAndLogin(token)
      .then(() => {
        if (mountedRef.current) {
          toast.success(t("auth.verify.successToast"));
          setStatus("success");
        }
      })
      .catch(() => {
        if (mountedRef.current) {
          toast.error(t("auth.verify.error"));
          setStatus("error");
        }
      });
  }, [token, verifyRootInviteAndLogin, isLoading, isAuthenticated, t]);

  if (!isLoading && isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (status === "success") {
    return <Navigate to="/" replace />;
  }

  /* Cover-less, like the other token waiting rooms: it resolves to a redirect,
     and there is nothing to say opposite a spinner. */
  if (status === "error") {
    return (
      <AuthShell>
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-muted-foreground">{t("auth.verify.error")}</p>
          <Button asChild variant="outline">
            <Link to="/login">{t("pages.common.back")}</Link>
          </Button>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="flex flex-col items-center gap-4 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-hidden />
        <p className="text-muted-foreground">{t("auth.verify.success")}</p>
      </div>
    </AuthShell>
  );
}
